﻿import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {HTMLElement | undefined} addToCartButtonContainer - The add to cart button container element, which may not be upgraded yet.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #variantCanPurchase = false;
  #availabilityInitialized = false;
  #variantSelectionPending = false;

  /** @type {{ productId: string | undefined, variantId: string, variantDisabled: boolean, button: HTMLButtonElement | null, buttonDisabled: boolean | undefined } | undefined} */
  #disconnectedContext;

  /** @type {MutationObserver | undefined} */
  #buttonReadyObserver;

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    // A reconnected form needs fresh listeners, not the previously aborted signal.
    this.#abortController.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    this.addEventListener('change', this.#syncBackorderConsentState, { signal });

    // Native button state is available before the child custom element upgrades.
    // Seed once, before consent writes; reconnecting must not treat our own
    // consent/pending-selection disabled state as server unavailability.
    // Different server markup/variant on a reused form must be checked anew.
    if (this.#disconnectedContext && !this.#variantSelectionPending && !this.#hasSameDisconnectedContext()) {
      this.#availabilityInitialized = false;
      this.#variantCanPurchase = false;
    }
    this.#disconnectedContext = undefined;
    this.#captureInitialAvailability();
    this.#stopWaitingForButton();
    const buttonContainer = this.refs.addToCartButtonContainer;
    if (!this.#availabilityInitialized && buttonContainer) {
      this.#buttonReadyObserver = new MutationObserver(() => {
        if (this.#captureInitialAvailability()) this.#syncBackorderConsentState();
      });
      this.#buttonReadyObserver.observe(buttonContainer, { childList: true, subtree: true });
    }
    this.#syncBackorderConsentState();
  }

  disconnectedCallback() {
    this.#rememberDisconnectedContext();
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#stopWaitingForButton();
  }

  /** @returns {HTMLButtonElement | null} */
  #addToCartButton() {
    return /** @type {HTMLButtonElement | null} */ (
      this.refs.addToCartButtonContainer?.querySelector('button[ref="addToCartButton"]') ?? null
    );
  }

  /** @param {boolean} disabled */
  #setAddToCartButtonDisabled(disabled) {
    const button = this.#addToCartButton();
    if (button) button.disabled = disabled;
  }

  #captureInitialAvailability() {
    if (this.#availabilityInitialized) return false;
    const button = this.#addToCartButton();
    if (!button) return false;

    this.#variantCanPurchase = Boolean(
      this.refs.variantId.value && !this.refs.variantId.disabled && !button.disabled
    );
    this.#availabilityInitialized = true;
    this.#stopWaitingForButton();
    return true;
  }

  #rememberDisconnectedContext() {
    const button = this.#addToCartButton();
    this.#disconnectedContext = {
      productId: this.dataset.productId,
      variantId: this.refs.variantId.value,
      variantDisabled: this.refs.variantId.disabled,
      button,
      buttonDisabled: button?.disabled,
    };
  }

  #hasSameDisconnectedContext() {
    const button = this.#addToCartButton();
    return this.#disconnectedContext?.productId === this.dataset.productId &&
      this.#disconnectedContext?.variantId === this.refs.variantId.value &&
      this.#disconnectedContext?.variantDisabled === this.refs.variantId.disabled &&
      this.#disconnectedContext?.button === button &&
      this.#disconnectedContext?.buttonDisabled === button?.disabled;
  }

  #stopWaitingForButton() {
    this.#buttonReadyObserver?.disconnect();
    this.#buttonReadyObserver = undefined;
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Send the add to cart information to the cart
    // Payment-terms forms can precede the purchase form inside this component.
    // Submit the actual owner of the selected variant, including quantity and consent.
    const form = this.refs.variantId.form;

    if (!form || !this.contains(form) || !form.contains(this.refs.variantId)) {
      throw new Error('Product form element missing');
    }

    if (this.#captureInitialAvailability()) this.#syncBackorderConsentState();
    if (!this.#variantCanPurchase || !this.refs.variantId.value || this.refs.variantId.disabled) return;

    if (this.#requiresBackorderConsent() && !this.#isBackorderConsentAccepted()) {
      const message = 'Please accept the special-order ETA before adding this item to cart.';
      this.#setLiveRegionText(message);
      this.#backorderConsentCheckbox()?.focus();
      return;
    }

    // Check if the add to cart button is disabled and do an early return if it is
    const addToCartButton = this.#addToCartButton();
    if (!addToCartButton || addToCartButton.disabled) return;

    this.#setBackorderConsentValues();

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          const addToCartButton = this.#addToCartButton();
          if (addToCartButton) {
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // Shopify's performance helper throws when a custom/sticky purchase action
        // reaches the form without the optional pointer-start mark. Cart completion
        // must stay independent from diagnostic timing.
        const startMark = 'cart-performance:add:user-action:start';
        if (performance.getEntriesByName(startMark, 'mark').length > 0) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const variant = event.detail.resource;

    const currentAddToCartButton = this.#addToCartButton();
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // A missing combination must never retain a previously purchasable variant ID.
    // Respect server-rendered restrictions as well as Shopify variant availability.
    this.#variantSelectionPending = false;
    this.#availabilityInitialized = true;
    this.#stopWaitingForButton();
    this.#variantCanPurchase = Boolean(
      variant?.id && variant.available === true && newAddToCartButton && !newAddToCartButton.disabled
    );
    variantId.value = variant?.id ?? '';
    variantId.disabled = !this.#variantCanPurchase;

    // Update the add to cart button text and icon
    if (currentAddToCartButton && newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    const currentBuyConfidence = this.querySelector('.p4m-buy-confidence');
    const newBuyConfidence = event.detail.data.html.querySelector('.p4m-buy-confidence');
    if (currentBuyConfidence && newBuyConfidence) {
      morph(currentBuyConfidence, newBuyConfidence);
    }

    const currentMobileBuySummary = this.querySelector('.p4m-mobile-buy-summary');
    const newMobileBuySummary = event.detail.data.html.querySelector('.p4m-mobile-buy-summary');
    if (currentMobileBuySummary && newMobileBuySummary) {
      morph(currentMobileBuySummary, newMobileBuySummary);
    }

    const currentBackorderConsent = this.querySelector('.p4m-backorder-consent');
    const newBackorderConsent = event.detail.data.html.querySelector('.p4m-backorder-consent');
    if (currentBackorderConsent && newBackorderConsent) {
      morph(currentBackorderConsent, newBackorderConsent);
    } else if (currentBackorderConsent && !newBackorderConsent) {
      currentBackorderConsent.remove();
    } else if (!currentBackorderConsent && newBackorderConsent) {
      variantId.insertAdjacentElement('afterend', /** @type {HTMLElement} */ (newBackorderConsent.cloneNode(true)));
    }

    const buyButtonsBlock = this.closest('.buy-buttons-block');
    const currentSpecialOrderRequest =
      buyButtonsBlock?.querySelector('.p4m-special-order-request') || this.querySelector('.p4m-special-order-request');
    const newSpecialOrderRequest = event.detail.data.html.querySelector('.p4m-special-order-request');
    if (currentSpecialOrderRequest && newSpecialOrderRequest) {
      morph(currentSpecialOrderRequest, newSpecialOrderRequest);
    } else if (currentSpecialOrderRequest && !newSpecialOrderRequest) {
      currentSpecialOrderRequest.remove();
    } else if (!currentSpecialOrderRequest && newSpecialOrderRequest) {
      (buyButtonsBlock || this).insertAdjacentElement('beforeend', /** @type {HTMLElement} */ (newSpecialOrderRequest.cloneNode(true)));
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (variant) {
      const productVariantMedia = variant.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    this.#syncBackorderConsentState();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.#variantSelectionPending = true;
    this.#availabilityInitialized = true;
    this.#stopWaitingForButton();
    this.#variantCanPurchase = false;
    this.refs.variantId.disabled = true;
    this.#setAddToCartButtonDisabled(true);
    this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
  };

  #syncBackorderConsentState = () => {
    // A change can fire before the readiness observer's microtask after insertion.
    this.#captureInitialAvailability();
    // Consent can authorize an eligible backorder, never an invalid/sold-out
    // combination or the old variant while a replacement is still loading.
    if (!this.#variantCanPurchase || !this.refs.variantId.value || this.refs.variantId.disabled || !this.#addToCartButton()) {
      this.#clearBackorderConsentValues();
      this.#setAddToCartButtonDisabled(true);
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      return;
    }

    if (!this.#requiresBackorderConsent()) {
      this.#setAddToCartButtonDisabled(false);
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      return;
    }

    const accepted = this.#isBackorderConsentAccepted();
    if (accepted) {
      this.#setBackorderConsentValues();
      this.#setAddToCartButtonDisabled(false);
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    } else {
      this.#clearBackorderConsentValues();
      this.#setAddToCartButtonDisabled(true);
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    }
  };

  #requiresBackorderConsent() {
    return this.querySelector('.p4m-backorder-consent[data-backorder-required="true"]') !== null;
  }

  #backorderConsentCheckbox() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-backorder-consent-checkbox]'));
  }

  #isBackorderConsentAccepted() {
    return this.#backorderConsentCheckbox()?.checked === true;
  }

  #setBackorderConsentValues() {
    if (!this.#requiresBackorderConsent() || !this.#isBackorderConsentAccepted()) return;

    const consentValue = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-backorder-consent-value]'));
    const consentAt = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-backorder-consent-at]'));
    if (consentValue) consentValue.value = 'accepted';
    if (consentAt && !consentAt.value) consentAt.value = new Date().toISOString();
  }

  #clearBackorderConsentValues() {
    const consentValue = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-backorder-consent-value]'));
    const consentAt = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-backorder-consent-at]'));
    if (consentValue) consentValue.value = '';
    if (consentAt) consentAt.value = '';
  }

}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
